import React, { useEffect, useState } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, Paper, styled, CircularProgress, Avatar } from '@mui/material';
import dayjs from 'dayjs';
import apiClient from '../../services/apiClient';

export const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: 12,
  padding: '6px 18px',
  border: '1px solid #90caf9',
  color: '#1976d2',
  fontSize: 14,
  transition: 'all 0.3s ease',
  '&.Mui-selected': {
    backgroundColor: '#1976d2',
    color: '#fff',
    '&:hover': { backgroundColor: '#1565c0' },
  },
  '&:hover': { backgroundColor: '#e3f2fd' },
}));

const CONSULT_TYPE_LABELS: Record<string, string> = {
  virtual: 'Teleconsult',
  home: 'Home Visit',
  clinic: 'Walk In',
};

const STATUS_COLORS: Record<string, string> = {
  new: '#2563EB', waiting: '#F59E0B', unpaid: '#F97316', ended: '#475569',
  started: '#4F46E5', inprogress: '#4338CA', cancelled: '#DC2626',
  completed: '#16A34A', confirmed: '#059669', paid: '#0D9488', pending: '#F59E0B',
  accepted: '#7C3AED', 'vet joined': '#8B5CF6', rescheduled: '#0E7490', expired: '#EF4444',
  refunded: '#22C55E', 'link ready': '#0EA5E9',
};

const getStatusClass = (slug?: string) => slug ? STATUS_COLORS[slug.toLowerCase()] || '#6B7280' : '#6B7280';

const TodayAppointmentsGrid = () => {
  const [role, setRole] = useState<'veterinarian' | 'service_provider'>('veterinarian');
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchTodayItems = async () => {
    try {
      setLoading(true);
      const params = { order_by: 'id', dir: 1, limit: 1000 };
      const queryString = new URLSearchParams(params as any).toString();
      const endpoint = role === 'veterinarian' ? 'consults' : 'service_orders';
      const response: any = await apiClient(`v1/resource/${endpoint}?${queryString}`, { method: 'GET' });

      const today = dayjs().format('YYYY-MM-DD');
      const dataKey = role === 'veterinarian' ? 'consults' : 'service_orders';
      const todayItems = response.data[dataKey]
        .filter((item: any) => dayjs(item.scheduled_at).format('YYYY-MM-DD') === today)
        .sort((a: any, b: any) => dayjs(a.scheduled_at).isBefore(dayjs(b.scheduled_at)) ? -1 : 1);

      setItems(todayItems);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTodayItems(); }, [role]);

  const renderCard = (item: any) => {
    const isVet = role === 'veterinarian';
    const time = dayjs(item.scheduled_at).format('hh:mm A');
    const statusSlug = isVet ? item.consult_current_status?.slug : item.service_status?.name;
    const statusColor = getStatusClass(statusSlug);

    // Extract common info
    const petName = isVet
      ? item.participants?.find((p: any) => p.role === 'subscriber')?.participant_info?.name || '-'
      : item?.pet?.additional_info?.name || '-';

    const parentName = isVet
      ? `${item.additional_info.pet_owner?.first_name || ''} ${item.additional_info.pet_owner?.last_name || ''}`.trim() || '-'
      : `${item?.owner?.first_name || ''} ${item?.owner?.last_name || ''}`.trim() || '-';

    const providerName = isVet
      ? item.participants?.find((p: any) => p.role === 'publisher')?.participant_info?.name || '-'
      : `${item.service_provider?.user?.first_name || ''} ${item.service_provider?.user?.last_name || ''}`.trim() || '-';

    const typeLabel = isVet
      ? (item.consult_type === 'service' ? item.additional_info.payment?.name : CONSULT_TYPE_LABELS[item.consult_type] || item.consult_type || '-')
      : item.service_charge_code?.service_name || '-';

    return (
      <Paper
        key={item.id}
        sx={{
          p: 2.5,
          borderRadius: 3,
          background: '#fff',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'transform 0.3s, box-shadow 0.3s',
          '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 28px rgba(0,0,0,0.12)' },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Status Chip */}
        <Box sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          px: 2,
          py: 0.5,
          borderRadius: 30,
          backgroundColor: statusColor,
          color: '#fff',
          fontSize: 12,
          fontWeight: 600,
        }}>
          {isVet ? item.consult_current_status?.name : item.service_status?.name || '-'}
        </Box>

        {/* Header with Pet Avatar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: '#1976d2', width: 40, height: 40 }}>{petName?.[0]}</Avatar>
          <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700 }}>{petName}</Typography>
        </Box>

        <Typography variant="body2" color="textSecondary">Pet Owner: {parentName}</Typography>
        <Typography variant="body2" color="textSecondary">{isVet ? 'Veterinarian' : 'Service Provider'}: {providerName}</Typography>
        <Typography variant="body2" color="textSecondary">{isVet ? 'Appointment Type' : 'Service'}: {typeLabel}</Typography>
        <Typography variant="body2" color="textSecondary">Scheduled Time: {time}</Typography>
      </Paper>
    );
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight={700}>
          Today's {role === 'veterinarian' ? 'Appointments' : 'Service Orders'}
        </Typography>
        <ToggleButtonGroup
          value={role}
          exclusive
          onChange={(_, newRole) => newRole && setRole(newRole)}
          sx={{ backgroundColor: '#f5f7fa', borderRadius: 12, p: 1 }}
        >
          <StyledToggleButton value="veterinarian">Veterinarian</StyledToggleButton>
          <StyledToggleButton value="service_provider">Service Provider</StyledToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Loading / Error / Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <CircularProgress size={36} />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
      ) : items.length ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
          {items.map(renderCard)}
        </Box>
      ) : (
        <Typography sx={{ mt: 4, color: '#999', fontStyle: 'italic', textAlign: 'center' }}>
          No {role === 'veterinarian' ? 'appointments' : 'service orders'} scheduled for today.
        </Typography>
      )}
    </Box>
  );
};

export default TodayAppointmentsGrid;
